"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import {
  fixed,
  type NumberFormatter,
  plain,
  sig,
} from "@/lib/tool-kit/biology-lab/format";
import {
  type CountError,
  type CountInput,
  type CountRegion,
  type CountResult,
  countCells,
  dilutionFromVolumes,
  REGION_SIDE_MM,
  STANDARD_DEPTH_MM,
} from "@/lib/tool-kit/biology-lab/hemocytometer";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const HEMOCYTOMETER_CALCULATOR_STRINGS = {
  liveLabel: "Live cells counted in each square",
  liveHint:
    "One number per square, separated by commas — for example the four corner squares.",
  deadLabel: "Dead (stained) cells in the same squares (optional)",
  deadHint: "Same order and number of squares; gives viability.",
  regionLabel: "What did you count?",
  regionLarge: "Large squares, 1 × 1 mm (0.1 µL each)",
  regionGroup: "Group squares, 0.2 × 0.2 mm (0.004 µL each)",
  regionSmall: "Smallest squares, 0.05 × 0.05 mm (0.00025 µL each)",
  depthLabel: "Chamber depth (mm)",
  depthHint: "0.1 mm for a Neubauer-improved chamber.",
  dilutionModeLabel: "Dilution",
  dilutionVolumes: "From the volumes I mixed",
  dilutionFactorMode: "I know the dilution factor",
  cellVolume: "Cell suspension (µL)",
  dyeVolume: "Trypan blue or diluent (µL)",
  priorFactor: "Dilution made before that (optional)",
  priorHint:
    "1 if you stained the suspension as it was; 10 if you first diluted it 1 in 10.",
  factor: "Dilution factor",
  factorHint: "2 for equal volumes of cells and trypan blue.",
  suspensionLabel: "Volume of suspension (mL, optional)",
  suspensionHint: "Gives the total number of live cells you have.",
  results: "Results",
  liveConc: "Live cells/mL",
  viability: "Viability",
  totalConc: "Total cells/mL (live + dead)",
  deadConc: "Dead cells/mL",
  totalCells: "Total live cells",
  meanSquare: "Mean live cells per square",
  squaresCounted: "Squares counted",
  countingError: "Counting error (random)",
  spreadLabel: "Spread between squares (standard deviation)",
  working: "The working",
  workingVolume:
    "volume of one square = {side}² mm × {depth} mm = {mm3} mm³ = {ml} mL",
  workingConc:
    "cells/mL = mean per square × dilution ÷ volume of one square = {mean} × {dilution} ÷ ({ml}) = {result}",
  workingViability:
    "viability = live ÷ (live + dead) = {live} ÷ {all} = {percent}%",
  workingDilution:
    "dilution = ({cells} + {dye}) ÷ {cells} × {prior} = {result}",
  copyResult: "Copy result",
  copied: "Copied!",
  copySummary: "{conc} live cells/mL",
  copyViability: "{percent}% viable",
  copyTotal: "{total} live cells in {volume} mL",
  lowCount:
    "Only {n} live cells were counted in total. Random counting error is about 1 ÷ √N, so {percent}% here — count more squares, or concentrate the sample, if you need better than that.",
  highSpread:
    "The squares differ more than random sampling alone would explain. Mix the suspension again, reload the chamber and recount.",
  errLive:
    "Enter the live cell count for at least one square (numbers of 0 or more).",
  errDead: "The dead cell counts must be numbers of 0 or more.",
  errSquares:
    "The live and dead lists must have the same number of squares ({live} live, {dead} dead).",
  errDilution: "The dilution factor must be 1 or more.",
  errDepth: "The chamber depth must be greater than 0.",
  enter: "Enter the counts, the dilution and the chamber depth.",
  borderNote:
    "Count a cell on a boundary in only two of the four sides of each square (top and left, say) so no cell is counted twice.",
} as const;

type Strings = typeof HEMOCYTOMETER_CALCULATOR_STRINGS;
type DilutionMode = "volumes" | "factor";

const ID = "hc";
const LOW_TOTAL = 100;
const MIN_SQUARES_FOR_SPREAD = 3;
const SPREAD_FACTOR = 2;

function errorText(
  code: CountError,
  strings: Strings,
  fmt: NumberFormatter,
  input: CountInput
): string {
  const messages: Record<CountError, string> = {
    live: strings.errLive,
    dead: strings.errDead,
    squares: t(strings.errSquares, {
      live: plain(fmt, input.live.length),
      dead: plain(fmt, input.dead?.length ?? 0),
    }),
    dilution: strings.errDilution,
    depth: strings.errDepth,
  };
  return messages[code];
}

function notes(
  r: CountResult,
  strings: Strings,
  fmt: NumberFormatter
): string[] {
  const out: string[] = [];
  if (r.totalLive < LOW_TOTAL && r.poissonPercent !== null) {
    out.push(
      t(strings.lowCount, {
        n: plain(fmt, r.totalLive),
        percent: fmt(r.poissonPercent, { maximumFractionDigits: 0 }),
      })
    );
  }
  const expectedCv = r.meanLive > 0 ? 100 / Math.sqrt(r.meanLive) : 0;
  if (
    r.squares >= MIN_SQUARES_FOR_SPREAD &&
    r.cvPercent !== null &&
    r.cvPercent > SPREAD_FACTOR * expectedCv
  ) {
    out.push(strings.highSpread);
  }
  return out;
}

function summaryText(
  r: CountResult,
  input: CountInput,
  strings: Strings,
  fmt: NumberFormatter
): string {
  const parts = [t(strings.copySummary, { conc: sig(fmt, r.liveConc, 4) })];
  if (r.viabilityPercent !== null) {
    parts.push(
      t(strings.copyViability, { percent: fixed(fmt, r.viabilityPercent, 1) })
    );
  }
  if (r.totalLiveCells !== null && input.suspensionMl !== null) {
    parts.push(
      t(strings.copyTotal, {
        total: sig(fmt, r.totalLiveCells, 4),
        volume: plain(fmt, input.suspensionMl),
      })
    );
  }
  return parts.join("; ");
}

function Working({
  r,
  input,
  fmt,
  strings,
}: {
  r: CountResult;
  input: CountInput;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const side = REGION_SIDE_MM[input.region];
  const mm3 = side * side * input.depthMm;
  const totalDead =
    input.dead === null ? 0 : input.dead.reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
      <p className="font-medium font-sans text-foreground">{strings.working}</p>
      <p>
        {t(strings.workingVolume, {
          side: plain(fmt, side),
          depth: plain(fmt, input.depthMm),
          mm3: plain(fmt, mm3),
          ml: sig(fmt, r.volumePerSquareMl, 4),
        })}
      </p>
      <p>
        {t(strings.workingConc, {
          mean: plain(fmt, r.meanLive),
          dilution: plain(fmt, input.dilution),
          ml: sig(fmt, r.volumePerSquareMl, 4),
          result: sig(fmt, r.liveConc, 4),
        })}
      </p>
      {r.viabilityPercent === null ? null : (
        <p>
          {t(strings.workingViability, {
            live: plain(fmt, r.totalLive),
            all: plain(fmt, r.totalLive + totalDead),
            percent: fixed(fmt, r.viabilityPercent, 1),
          })}
        </p>
      )}
    </div>
  );
}

function Results({
  input,
  fmt,
  strings,
}: {
  input: CountInput;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const outcome = countCells(input);
  if ("error" in outcome) {
    return (
      <ErrorNotice>{errorText(outcome.error, strings, fmt, input)}</ErrorNotice>
    );
  }
  const r = outcome.result;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.liveConc}
          value={sig(fmt, r.liveConc, 4)}
        />
        {r.viabilityPercent === null ? null : (
          <StatCard
            label={strings.viability}
            value={`${fixed(fmt, r.viabilityPercent, 1)}%`}
          />
        )}
        {r.totalConc === null ? null : (
          <StatCard
            label={strings.totalConc}
            value={sig(fmt, r.totalConc, 4)}
          />
        )}
        {r.totalLiveCells === null ? null : (
          <StatCard
            label={strings.totalCells}
            value={sig(fmt, r.totalLiveCells, 4)}
          />
        )}
        <StatCard
          label={strings.meanSquare}
          value={fmt(r.meanLive, { maximumFractionDigits: 2 })}
        />
        <StatCard
          label={strings.squaresCounted}
          value={plain(fmt, r.squares)}
        />
        {r.poissonPercent === null ? null : (
          <StatCard
            label={strings.countingError}
            value={`±${fmt(r.poissonPercent, { maximumFractionDigits: 1 })}%`}
          />
        )}
        {r.sdLive === null ? null : (
          <StatCard
            label={strings.spreadLabel}
            value={fmt(r.sdLive, { maximumFractionDigits: 2 })}
          />
        )}
      </StatGrid>
      {notes(r, strings, fmt).map((note) => (
        <Notice key={note}>{note}</Notice>
      ))}
      <Working fmt={fmt} input={input} r={r} strings={strings} />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summaryText(r, input, strings, fmt)}
      />
      <p className="text-muted-foreground text-xs">{strings.borderNote}</p>
    </>
  );
}

export default function HemocytometerCalculatorWidget() {
  const strings = useToolUi(HEMOCYTOMETER_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [live, setLive] = useState("52, 47, 55, 49");
  const [dead, setDead] = useState("6, 4, 7, 5");
  const [region, setRegion] = useState<CountRegion>("large");
  const [depth, setDepth] = useState(String(STANDARD_DEPTH_MM));
  const [mode, setMode] = useState<DilutionMode>("volumes");
  const [cellUl, setCellUl] = useState("10");
  const [dyeUl, setDyeUl] = useState("10");
  const [prior, setPrior] = useState("1");
  const [factor, setFactor] = useState("2");
  const [suspension, setSuspension] = useState("5");

  const depthValue = parseMeasuredNumber(depth);
  const dilutionValue =
    mode === "factor"
      ? parseMeasuredNumber(factor)
      : dilutionFromVolumes(
          parseMeasuredNumber(cellUl) ?? 0,
          parseMeasuredNumber(dyeUl) ?? 0,
          parseMeasuredNumber(prior) ?? 1
        );
  const input: CountInput | null =
    depthValue === null || dilutionValue === null
      ? null
      : {
          live: parseNumberList(live),
          dead: dead.trim() === "" ? null : parseNumberList(dead),
          region,
          depthMm: depthValue,
          dilution: dilutionValue,
          suspensionMl: parseMeasuredNumber(suspension),
        };

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.liveHint}
          id={`${ID}-live`}
          label={strings.liveLabel}
          onChange={setLive}
          value={live}
        />
        <TextField
          hint={strings.deadHint}
          id={`${ID}-dead`}
          label={strings.deadLabel}
          onChange={setDead}
          value={dead}
        />
        <SelectField
          id={`${ID}-region`}
          label={strings.regionLabel}
          onChange={setRegion}
          options={[
            { value: "large", label: strings.regionLarge },
            { value: "group", label: strings.regionGroup },
            { value: "small", label: strings.regionSmall },
          ]}
          value={region}
        />
        <TextField
          hint={strings.depthHint}
          id={`${ID}-depth`}
          label={strings.depthLabel}
          numeric
          onChange={setDepth}
          value={depth}
        />
      </div>
      <PillGroup
        label={strings.dilutionModeLabel}
        name={`${ID}-dilmode`}
        onChange={setMode}
        options={[
          { value: "volumes", label: strings.dilutionVolumes },
          { value: "factor", label: strings.dilutionFactorMode },
        ]}
        value={mode}
      />
      {mode === "volumes" ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id={`${ID}-cell`}
            label={strings.cellVolume}
            numeric
            onChange={setCellUl}
            value={cellUl}
          />
          <TextField
            id={`${ID}-dye`}
            label={strings.dyeVolume}
            numeric
            onChange={setDyeUl}
            value={dyeUl}
          />
          <TextField
            hint={strings.priorHint}
            id={`${ID}-prior`}
            label={strings.priorFactor}
            numeric
            onChange={setPrior}
            value={prior}
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            hint={strings.factorHint}
            id={`${ID}-factor`}
            label={strings.factor}
            numeric
            onChange={setFactor}
            value={factor}
          />
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          hint={strings.suspensionHint}
          id={`${ID}-susp`}
          label={strings.suspensionLabel}
          numeric
          onChange={setSuspension}
          value={suspension}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {input ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <Results fmt={fmt} input={input} strings={strings} />
          </div>
        ) : (
          <Notice>{strings.enter}</Notice>
        )}
      </section>
    </>
  );
}
